/**
 * /login/magic?token=... — one-tap sign-in landing page.
 *
 * Consumes a magic-link JWT emitted by the admin generator, exchanges
 * it via POST /api/auth/magic-login for a regular auth token, and
 * navigates to the app. On error, falls back to the normal login page
 * with an inline explanation.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MagicLoginPage() {
  const [params] = useSearchParams();
  const { code: pathCode } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("signing_in"); // signing_in | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const code = pathCode || params.get("code");
    const legacyToken = params.get("token");
    // Optional `?next=/absorption` deep-link — used by the admin
    // concierge to send someone straight to a specific screen after
    // sign-in. Only accept same-origin absolute paths.
    const rawNext = params.get("next") || "";
    const safeNext = /^\/(?!\/)[^\s?#]*$/.test(rawNext) ? rawNext : "/";
    if (!code && !legacyToken) {
      setStatus("error");
      setErrorMsg("This link is missing its access code.");
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/magic-login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(code ? { code } : { token: legacyToken }),
        });
        if (!r.ok) {
          const detail = (await r.json())?.detail || `HTTP ${r.status}`;
          throw new Error(detail);
        }
        const data = await r.json();
        localStorage.setItem("reclaim_token", data.token);
        localStorage.setItem("reclaim_user", JSON.stringify(data.user));
        navigate(safeNext, { replace: true });
      } catch (e) {
        setStatus("error");
        setErrorMsg(e.message || "Sign-in failed. Please ask for a fresh link.");
      }
    })();
  }, [params, pathCode, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{
        background: 'radial-gradient(ellipse at top, #14201C 0%, #0A100E 60%, #050807 100%)',
        color: '#F5E7C3',
        fontFamily: '"Manrope", sans-serif',
      }}
      data-testid="magic-login-page"
    >
      <div className="max-w-sm text-center">
        {status === "signing_in" && (
          <>
            <div className="text-3xl italic mb-3" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
              Welcome back
            </div>
            <div className="text-sm opacity-60">Signing you in…</div>
            <div className="mt-6 opacity-40 text-xs italic">
              (This tab is exchanging your invite link for a session.)
            </div>
          </>
        )}
        {status === "error" && (
          <>
            <div className="text-3xl italic mb-3" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
              Link couldn't be used
            </div>
            <div className="text-sm opacity-70 mb-6">{errorMsg}</div>
            <button
              onClick={() => navigate('/login')}
              className="px-5 py-2 rounded-full text-sm border transition-all"
              style={{
                background: 'rgba(215, 187, 122, 0.15)',
                borderColor: 'rgba(215, 187, 122, 0.35)',
                color: '#F5E7C3',
              }}
              data-testid="magic-login-fallback"
            >
              Sign in with email
            </button>
          </>
        )}
      </div>
    </div>
  );
}
